import * as THREE from 'three';
import { TILE_SIZE } from '../../labyrinth/Labyrinth';
import { applyStylizedShading, type LightmapBindings } from '../StylizedShading';

export const TURRET_R = TILE_SIZE * 0.22;
export const TURRET_H = TILE_SIZE * 0.12;

/** Default tint when nothing else is overriding (used as exploring/idle). */
export const TURRET_BASE_COLOR = 0x3a3e46;

const SHADING = {
  lightmapNormalOffset: false,
  keyLightStrength: 0.30,
  rimStrength: 0.55,
  rimPower: 2.5,
  verticalAOStrength: 0.22,
} as const;

const SKIN = {
  panelScale: 0.70, etchStrength: 0.42, ledDensity: 0.18,
  conduitStrength: 0.08, trimHue: 0.03, // warm red-orange (combat focal)
} as const;

export interface RobotTurretPart {
  /** Turret mesh — the rig parents this under a yaw pivot at the appropriate height. */
  readonly object: THREE.Object3D;
  /** Override the base material tint (used by the rig to express robot state). */
  setStateColor(hex: number): void;
  dispose(): void;
}

/**
 * Procedural turret cylinder. The base material color is mutated at runtime
 * to express the robot's state (exploring / combat / looting / etc.) — see
 * `setStateColor`. To swap with a Blender model, keep `TURRET_R` / `TURRET_H`
 * accurate (the rig uses them for barrel attachment offset and for the Y
 * placement of the turret pivot above the chassis) and route `setStateColor`
 * to whichever sub-mesh you want to recolor.
 */
export function createRobotTurret(lightmap: LightmapBindings): RobotTurretPart {
  const geo = new THREE.CylinderGeometry(TURRET_R, TURRET_R, TURRET_H, 20);
  const mat = new THREE.MeshBasicMaterial({ color: TURRET_BASE_COLOR });
  applyStylizedShading(mat, { lightmap, ...SHADING, skin: { seed: 11, ...SKIN } });
  const mesh = new THREE.Mesh(geo, mat);
  return {
    object: mesh,
    setStateColor: (hex: number) => { mat.color.setHex(hex); },
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
}
